"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import { RefreshCw, RotateCcw, Play } from "lucide-react";
import { Card, CardContent } from "@/app/_module/components/ui/card";
import { Button } from "@/app/_module/components/ui/button";
import { useTour } from "./TourProvider";
import { WelcomeTourModal } from "./WelcomeTourModal";
import { INTERACTIVE_TOURS_ENABLED } from "@/app/_module/config/tours";
import { useBranding } from "@/app/_module/store/contexts/brandingContext";

const EASE = [0.22, 1, 0.36, 1] as [number, number, number, number];

/**
 * ReplayToursSection Component
 * Simple reset button to restart the onboarding tour
 */
export function ReplayToursSection() {
  const { resetAllTours, refreshProgress, isLoading: contextLoading } = useTour();
  const { branding } = useBranding();
  const [isResetting, setIsResetting] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);

  // The guided tour drives DESKTOP chrome (and exits to /dashboard, which
  // would dump the shell onto the desktop app) — hide the card on /m.
  // After hooks on purpose: an early return above them breaks hook order.
  const isShell =
    typeof window !== "undefined" && window.location.pathname.startsWith("/m");
  if (isShell) return null;

  // Tours are off: this card is the one place to re-watch the demo video.
  if (!INTERACTIVE_TOURS_ENABLED) {
    return (
      <>
        <Card className="overflow-hidden" data-tour="replay-tours">
          <CardContent className="flex items-center justify-between gap-4 p-5">
            <div>
              <p className="text-sm font-semibold text-gray-900">Product demo</p>
              <p className="mt-0.5 text-xs text-gray-500">The two-minute walkthrough from your first sign-in.</p>
            </div>
            <Button size="sm" variant="outline" className="gap-1.5 shrink-0" onClick={() => setDemoOpen(true)}>
              <Play className="h-3.5 w-3.5" />
              Watch the demo
            </Button>
          </CardContent>
        </Card>
        <WelcomeTourModal
          isOpen={demoOpen}
          toursEnabled={false}
          initialVideo
          onStartTour={() => setDemoOpen(false)}
          onSkip={() => setDemoOpen(false)}
        />
      </>
    );
  }

  const handleRestartTour = async () => {
    setIsResetting(true);
    try {
      await resetAllTours();
      await refreshProgress();
      window.location.href = "/dashboard";
    } catch (error) {
      console.error("Failed to restart tour:", error);
      setIsResetting(false);
    }
  };

  return (
    <Card className="overflow-hidden" data-tour="replay-tours">
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: EASE }}
        className="px-6 pt-6 pb-4"
      >
        <div className="flex items-center gap-4">
          <div className="flex items-center justify-center h-11 w-11 rounded-xl bg-primary text-primary-foreground shadow-sm">
            <RefreshCw className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-xl font-semibold tracking-tight">Onboarding Tour</h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              Restart the guided tour to learn about {branding.brandName}&apos;s features
            </p>
          </div>
        </div>

        <div className="mt-5 h-px bg-gradient-to-r from-primary/25 via-primary/10 to-transparent" />
      </motion.div>

      <CardContent className="pt-0 pb-6">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1, ease: EASE }}
        >
          <Button
            variant="outline"
            onClick={handleRestartTour}
            disabled={isResetting || contextLoading}
            className="gap-2"
          >
            {isResetting ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <RotateCcw className="w-4 h-4" />
            )}
            Restart Onboarding Tour
          </Button>
        </motion.div>
      </CardContent>
    </Card>
  );
}

export default ReplayToursSection;
